// components/WhatsAppCallButton.js
// ✅ ENHANCED: Country Code Box Integrated in Phone Input Field + All Previous Features
// Floating WhatsApp call button with WebRTC + Meta Cloud API integration + Country Code Selector

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Phone, PhoneOff, X, Shield, ChevronDown } from 'lucide-react';
import { toast } from 'react-hot-toast';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { getDisplayPhoneNumber, maskPhoneNumber } from '../utils/numberMasking';
import '../styles/WhatsAppCallButton.css';

// ── Country Codes Database ───────────────────────────────────────────────────
const COUNTRY_CODES = [
  { code: '+1', country: 'United States', flag: '🇺🇸', iso: 'US' },
  { code: '+1', country: 'Canada', flag: '🇨🇦', iso: 'CA' },
  { code: '+44', country: 'United Kingdom', flag: '🇬🇧', iso: 'GB' },
  { code: '+91', country: 'India', flag: '🇮🇳', iso: 'IN' },
  { code: '+86', country: 'China', flag: '🇨🇳', iso: 'CN' },
  { code: '+81', country: 'Japan', flag: '🇯🇵', iso: 'JP' },
  { code: '+82', country: 'South Korea', flag: '🇰🇷', iso: 'KR' },
  { code: '+33', country: 'France', flag: '🇫🇷', iso: 'FR' },
  { code: '+49', country: 'Germany', flag: '🇩🇪', iso: 'DE' },
  { code: '+39', country: 'Italy', flag: '🇮🇹', iso: 'IT' },
  { code: '+34', country: 'Spain', flag: '🇪🇸', iso: 'ES' },
  { code: '+31', country: 'Netherlands', flag: '🇳🇱', iso: 'NL' },
  { code: '+41', country: 'Switzerland', flag: '🇨🇭', iso: 'CH' },
  { code: '+43', country: 'Austria', flag: '🇦🇹', iso: 'AT' },
  { code: '+46', country: 'Sweden', flag: '🇸🇪', iso: 'SE' },
  { code: '+47', country: 'Norway', flag: '🇳🇴', iso: 'NO' },
  { code: '+45', country: 'Denmark', flag: '🇩🇰', iso: 'DK' },
  { code: '+358', country: 'Finland', flag: '🇫🇮', iso: 'FI' },
  { code: '+48', country: 'Poland', flag: '🇵🇱', iso: 'PL' },
  { code: '+420', country: 'Czech Republic', flag: '🇨🇿', iso: 'CZ' },
  { code: '+36', country: 'Hungary', flag: '🇭🇺', iso: 'HU' },
  { code: '+40', country: 'Romania', flag: '🇷🇴', iso: 'RO' },
  { code: '+30', country: 'Greece', flag: '🇬🇷', iso: 'GR' },
  { code: '+90', country: 'Turkey', flag: '🇹🇷', iso: 'TR' },
  { code: '+380', country: 'Ukraine', flag: '🇺🇦', iso: 'UA' },
  { code: '+7', country: 'Russia', flag: '🇷🇺', iso: 'RU' },
  { code: '+972', country: 'Israel', flag: '🇮🇱', iso: 'IL' },
  { code: '+966', country: 'Saudi Arabia', flag: '🇸🇦', iso: 'SA' },
  { code: '+971', country: 'United Arab Emirates', flag: '🇦🇪', iso: 'AE' },
  { code: '+60', country: 'Malaysia', flag: '🇲🇾', iso: 'MY' },
  { code: '+65', country: 'Singapore', flag: '🇸🇬', iso: 'SG' },
  { code: '+66', country: 'Thailand', flag: '🇹🇭', iso: 'TH' },
  { code: '+84', country: 'Vietnam', flag: '🇻🇳', iso: 'VN' },
  { code: '+62', country: 'Indonesia', flag: '🇮🇩', iso: 'ID' },
  { code: '+63', country: 'Philippines', flag: '🇵🇭', iso: 'PH' },
  { code: '+61', country: 'Australia', flag: '🇦🇺', iso: 'AU' },
  { code: '+64', country: 'New Zealand', flag: '🇳🇿', iso: 'NZ' },
  { code: '+55', country: 'Brazil', flag: '🇧🇷', iso: 'BR' },
  { code: '+54', country: 'Argentina', flag: '🇦🇷', iso: 'AR' },
  { code: '+56', country: 'Chile', flag: '🇨🇱', iso: 'CL' },
  { code: '+57', country: 'Colombia', flag: '🇨🇴', iso: 'CO' },
  { code: '+52', country: 'Mexico', flag: '🇲🇽', iso: 'MX' },
];

// ── WhatsApp API base URLs ───────────────────────────────────────────────────
const normalizeWaBaseUrl = (baseUrl) => {
  if (!baseUrl) return '';

  return baseUrl.replace(/\/api\/?$/, '');
};

const getWaApiBase = () => {
  let base =
    process.env.REACT_APP_WA_API_URL ||
    normalizeWaBaseUrl(process.env.REACT_APP_API_URL) ||
    'http://localhost:5000';

  // DYNAMIC FALLBACK: If the URL points to localhost:5000 (Node.js) instead of 8000 (Python),
  // dynamically redirect it to port 8000. This handles cases where dev server was not restarted.
  if (base.includes('localhost:5000') || base.includes('127.0.0.1:5000')) {
    console.log('[WA Call] 💡 Dynamic Port Fallback: Redirecting from 5000 to Python backend at 8000');
    base = base.replace(':5000', ':8000');
  }
  return base;
};

const WA_API_BASE = getWaApiBase();
// WebSocket uses wss:// for https hosts, ws:// for http (localhost)
const WA_WS_BASE = WA_API_BASE.replace(/^https:/, 'wss:').replace(/^http:/, 'ws:');
console.log('[WA Call] API base:', WA_API_BASE);

// FIX: Added multiple STUN server fallbacks for robust ICE candidate gathering
const ICE_SERVERS = {
  iceServers: [
    { urls: 'stun:stun.l.google.com:19302' },
    { urls: 'stun:stun1.l.google.com:19302' },
    { urls: 'stun:stun2.l.google.com:19302' },
    { urls: 'stun:stun3.l.google.com:19302' },
    { urls: 'stun:stun4.l.google.com:19302' },
    { urls: 'stun:stun.cloudflare.com:3478' }
  ],
  iceCandidatePoolSize: 10
};

function isValidE164(number) {
  return /^\d{7,15}$/.test(number);
}

function sanitizeNumber(raw) {
  return raw.replace(/[^\d]/g, '');
}

const parsePhoneNumber = (numberStr) => {
  if (!numberStr) return { country: null, number: '' };
  
  let clean = numberStr.replace(/[^\d+]/g, '');
  
  // Default to India (+91)
  let matchedCountry = COUNTRY_CODES.find(c => c.iso === 'IN') || COUNTRY_CODES[0];
  let parsedNumber = clean;
  
  if (clean.startsWith('+')) {
    const sortedCountries = [...COUNTRY_CODES].sort((a, b) => b.code.length - a.code.length);
    for (const c of sortedCountries) {
      if (clean.startsWith(c.code)) {
        matchedCountry = c;
        parsedNumber = clean.substring(c.code.length);
        break;
      }
    }
  } else if (clean.length > 10) {
    const sortedCountries = [...COUNTRY_CODES].sort((a, b) => b.code.length - a.code.length);
    for (const c of sortedCountries) {
      const codeWithoutPlus = c.code.replace('+', '');
      if (clean.startsWith(codeWithoutPlus)) {
        matchedCountry = c;
        parsedNumber = clean.substring(codeWithoutPlus.length);
        break;
      }
    }
  }
  
  return {
    country: matchedCountry,
    number: parsedNumber
  };
};

const WhatsAppCallButton = () => {
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [selectedCountry, setSelectedCountry] = useState(COUNTRY_CODES.find(c => c.iso === 'IN') || COUNTRY_CODES[0]);
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const [countrySearch, setCountrySearch] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [callStatus, setCallStatus] = useState('idle');
  const [callId, setCallId] = useState(null);
  const [callDuration, setCallDuration] = useState(0);
  const [masking, setMasking] = useState(false);
  const [maskingLoaded, setMaskingLoaded] = useState(false);
  // FIX: Replaced isTyping timer with clean focus-based state to eliminate digit-deletion glitch
  const [isFocused, setIsFocused] = useState(false);
  // ✅ SECURE MASKING: Tracks if number was auto-populated from database/CRM or typed manually
  const [isPrePopulated, setIsPrePopulated] = useState(false);
  const [customCredentials, setCustomCredentials] = useState(null);

  const panelRef = useRef(null);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const peerConnectionRef = useRef(null);
  const localStreamRef = useRef(null);
  const wsRef = useRef(null);
  const timerRef = useRef(null);
  const audioRef = useRef(null);
  const maskingCheckIntervalRef = useRef(null);
  // typingTimeoutRef removed — no longer needed with focus-based masking

  const browserIdRef = useRef("browser_" + Math.random().toString(36).slice(2, 10));

  // ── Filter countries for dropdown ──
  const filteredCountries = countrySearch.trim()
    ? COUNTRY_CODES.filter(c =>
      c.country.toLowerCase().includes(countrySearch.toLowerCase()) ||
      c.code.includes(countrySearch) ||
      c.iso.toLowerCase().includes(countrySearch.toLowerCase())
    )
    : COUNTRY_CODES;

  // ── Focus input on open ──
  useEffect(() => {
    if (isOpen && inputRef.current && callStatus === 'idle') {
      setTimeout(() => inputRef.current?.focus(), 120);
    }
  }, [isOpen, callStatus]);

  // ── Click outside to close (only if idle) ──
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        panelRef.current &&
        !panelRef.current.contains(e.target) &&
        callStatus === 'idle'
      ) {
        setIsOpen(false);
        setShowCountryDropdown(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, callStatus]);

  // ── Remote trigger listener ──
  useEffect(() => {
    const handleRemoteWACall = (e) => {
      const { phoneNumber: numberToCall, clientId, userType: customUserType } = e.detail;
      const parsed = parsePhoneNumber(numberToCall);
      if (parsed.country) {
        setSelectedCountry(parsed.country);
      }
      setPhoneNumber(parsed.number);
      setIsPrePopulated(true); // ✅ SECURE MASKING: Mark as pre-populated from CRM
      setIsOpen(true);
      if (clientId) {
        setCustomCredentials({ userId: clientId, userType: customUserType || 'client' });
      } else {
        setCustomCredentials(null);
      }

      const menuToggle = document.querySelector('.floating-menu-toggle');
      const menuContainer = document.querySelector('.floating-interaction-menu');
      if (menuContainer) {
        if (!menuContainer.classList.contains('open')) {
          menuContainer.classList.add('open');
        }
      }
      if (menuToggle && !menuToggle.classList.contains('open')) {
        menuToggle.classList.add('open');
      }
    };
    window.addEventListener('INITIATE_WA_CALL', handleRemoteWACall);
    return () => window.removeEventListener('INITIATE_WA_CALL', handleRemoteWACall);
  }, []);

  // Masking is disabled globally in the dashboard
  const fetchMaskingConfig = useCallback(async () => {
    setMasking(false);
    setMaskingLoaded(true);
  }, []);

  // ✅ Fetch masking config on mount
  useEffect(() => {
    console.log('[WA Call] Component mounted, fetching masking config...');
    fetchMaskingConfig();
  }, [fetchMaskingConfig]);

  // ✅ Periodic check for masking config changes (every 10 seconds)
  useEffect(() => {
    maskingCheckIntervalRef.current = setInterval(() => {
      console.log('[WA Call] Periodic masking config check...');
      fetchMaskingConfig();
    }, 10000);

    return () => {
      if (maskingCheckIntervalRef.current) {
        clearInterval(maskingCheckIntervalRef.current);
      }
    };
  }, [fetchMaskingConfig]);

  // ✅ Fetch config when panel opens (ensure fresh data)
  useEffect(() => {
    if (isOpen) {
      console.log('[WA Call] Panel opened, refreshing masking config...');
      fetchMaskingConfig();
    }
  }, [isOpen, fetchMaskingConfig]);

  // ── WebSocket listener for call events from webhook ──
  useEffect(() => {
    let reconnectTimeout = null;
    let ws = null;

    const connectWS = () => {
      const pythonWsUrl = `${WA_WS_BASE}/api/wa-call/ws`;
      console.log('[WA Call] Connecting WS to:', pythonWsUrl);

      ws = new WebSocket(pythonWsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        console.log('[WA Call] WS Connected');
        ws.send(JSON.stringify({ type: "register", id: browserIdRef.current }));
      };

      ws.onclose = () => {
        console.log('[WA Call] WS Disconnected — retrying...');
        reconnectTimeout = setTimeout(connectWS, 3000);
      };

      ws.onerror = (e) => console.error("[WA Call] WS error:", e);

      ws.onmessage = async (event) => {
        try {
          const msg = JSON.parse(event.data);
          console.log("[WA Call] WS message:", msg);

          switch (msg.type) {
            case "registered":
              console.log("✅ Registered with server as:", msg.id);
              break;

            case "call_status":
              console.log('[WA Call] WS → Call status:', msg.status);
              if (msg.status === 'ringing' || msg.status === 'RINGING') {
                setCallStatus('ringing');
              } else if (msg.status === 'answered' || msg.status === 'ACCEPTED') {
                setCallStatus('connected');
                startTimer();
              } else if (msg.status === 'rejected' || msg.status === 'REJECTED') {
                setCallStatus('error');
                toast.error('Call was rejected by the user');
                cleanupWebRTC();
              }
              break;

            case "sdp_answer":
              console.log('[WA Call] WS → SDP Answer received');
              await applyRemoteSdp(msg.sdp, msg.sdpType);
              break;

          case "call_ended":
              console.log('[WA Call] WS → Call terminated:', msg);
              toast('Call ended', { icon: '📞', duration: 3000 });
              resetCallState(true);
              break;

            default:
              break;
          }
        } catch (err) {
          console.error('[WA Call] WS message parse error:', err);
        }
      };
    };

    connectWS();

    return () => {
      clearTimeout(reconnectTimeout);
      if (ws) {
        ws.onclose = null;
        ws.close();
      }
    };
  }, []);

  // ── Timer ──
  const startTimer = useCallback(() => {
    setCallDuration(0);
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setCallDuration((prev) => prev + 1);
    }, 1000);
  }, []);

  const stopTimer = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  };

  // ── WebRTC Functions ──
  const getLocalStream = async () => {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
    localStreamRef.current = stream;
    return stream;
  };

  const generateSdpOffer = async () => {
    const pc = new RTCPeerConnection(ICE_SERVERS);
    peerConnectionRef.current = pc;

    const stream = localStreamRef.current;
    if (stream) {
      stream.getTracks().forEach((track) => {
        pc.addTrack(track, stream);
      });
    }

    // FIX: Robust audio track routing — manually construct MediaStream from the raw track.
    // In many browsers, event.streams[0] is empty/undefined when the track first arrives.
    // Attaching the raw track directly guarantees audio always flows to the <audio> element.
    pc.ontrack = (event) => {
      console.log('[WA Call] 🔊 Remote track received — kind:', event.track.kind, '| streams:', event.streams.length);
      try {
        if (audioRef.current) {
          let remoteStream;
          if (event.streams && event.streams[0]) {
            remoteStream = event.streams[0];
            console.log('[WA Call] ✅ Using event.streams[0]');
          } else {
            // Fallback: manually build a MediaStream from the raw track
            remoteStream = new MediaStream([event.track]);
            console.log('[WA Call] ✅ Built MediaStream manually from raw track (streams[0] was empty)');
          }
          audioRef.current.srcObject = remoteStream;
          // Force playback — bypasses browser autoplay policy
          const playPromise = audioRef.current.play();
          if (playPromise !== undefined) {
            playPromise
              .then(() => console.log('[WA Call] ✅ Audio playback started successfully'))
              .catch(e => console.error('[WA Call] ❌ Audio play() was blocked by browser:', e.name, e.message));
          }
        } else {
          console.error('[WA Call] ❌ audioRef.current is null — audio element not mounted yet');
        }
      } catch (trackErr) {
        console.error('[WA Call] ❌ Error in ontrack handler:', trackErr);
      }
    };

    // FIX: Log ICE connection state changes so we can see exactly why the call drops
    pc.oniceconnectionstatechange = () => {
      const state = pc.iceConnectionState;
      console.log('[WA Call] ICE connection state changed →', state);
      if (state === 'failed') {
        console.error('[WA Call] ❌ ICE FAILED — likely a firewall or NAT issue blocking STUN. Audio cannot flow.');
        toast.error('Audio connection failed — network firewall may be blocking the call.', { duration: 6000 });
      } else if (state === 'disconnected') {
        console.warn('[WA Call] ⚠️ ICE DISCONNECTED — network interruption detected');
      } else if (state === 'connected') {
        console.log('[WA Call] ✅ ICE CONNECTED — audio path is established');
      }
    };

    pc.onconnectionstatechange = () => {
      console.log('[WA Call] Peer connection state →', pc.connectionState);
      if (pc.connectionState === 'failed') {
        console.error('[WA Call] ❌ PEER CONNECTION FAILED');
      }
    };

    const offer = await pc.createOffer({ offerToReceiveAudio: true });
    await pc.setLocalDescription(offer);

    // FIX: Wait properly for ICE gathering to fully complete (up to 8 seconds).
    // The old 3s timeout was too short — if it expired before candidates were gathered,
    // Meta would receive an SDP with no network addresses and couldn't route audio back.
    await new Promise(resolve => {
      if (pc.iceGatheringState === 'complete') {
        console.log('[WA Call] ICE gathering was already complete');
        return resolve();
      }
      pc.onicegatheringstatechange = () => {
        console.log('[WA Call] ICE gathering state →', pc.iceGatheringState);
        if (pc.iceGatheringState === 'complete') resolve();
      };
      // Safety timeout — increased to 8s from 3s
      setTimeout(() => {
        console.warn('[WA Call] ⚠️ ICE gathering timed out after 8s — sending SDP with partial candidates');
        resolve();
      }, 8000);
    });

    const sdp = pc.localDescription.sdp;
    const candidateCount = (sdp.match(/a=candidate:/g) || []).length;
    console.log(`[WA Call] SDP ready — ${candidateCount} ICE candidates gathered`);
    if (candidateCount === 0) {
      console.error('[WA Call] ❌ Zero ICE candidates! STUN failed. Audio WILL NOT work. Check firewall/network.');
    }
    return sdp;
  };

  const applyRemoteSdp = async (answerSdp, sdpType) => {
    const pc = peerConnectionRef.current;
    if (!pc) {
      console.error('[WA Call] No peer connection to apply SDP answer');
      return;
    }

    try {
      const answer = new RTCSessionDescription({ type: sdpType || 'answer', sdp: answerSdp });
      await pc.setRemoteDescription(answer);
      console.log('[WA Call] ✅ Remote SDP applied — audio connection live');
      setCallStatus('connected');
    } catch (err) {
      console.error('[WA Call] ❌ Failed to apply remote SDP:', err);
      toast.error('Failed to establish audio connection');
    }
  };

  const cleanupWebRTC = () => {
    if (peerConnectionRef.current) {
      peerConnectionRef.current.close();
      peerConnectionRef.current = null;
    }
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach((track) => track.stop());
      localStreamRef.current = null;
    }
    if (audioRef.current) {
      audioRef.current.srcObject = null;
    }
  };

  const resetCallState = (keepPrePopulated = false) => {
    cleanupWebRTC();
    stopTimer();
    setCallStatus('idle');
    setCallId(null);
    setCallDuration(0);
    setLoading(false);
    if (!keepPrePopulated) setIsPrePopulated(false);
    setCustomCredentials(null);
  };

  // ── Handlers ──
  const handleSendPermission = async () => {
    const clean = selectedCountry.code.replace('+', '') + sanitizeNumber(phoneNumber);
    const userType = customCredentials ? customCredentials.userType : ((user?.role === 'admin' || user?.role === 'super_admin') ? 'admin' : 'client');
    const userId = customCredentials ? customCredentials.userId : (user?.id || user?.client_id || user?.admin_id);

    if (!sanitizeNumber(phoneNumber)) {
      setError('Please enter a phone number');
      return;
    }
    if (!isValidE164(clean)) {
      setError('Invalid phone number for selected country');
      return;
    }

    setError('');
    setLoading(true);

    try {
      const res = await fetch(`${WA_API_BASE}/api/wa-call/permission`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ to: clean, userId, userType })
      });
      const data = await res.json();

      if (res.ok || data.success) {
        // FIX: Handle case where permission is already permanently granted (Meta error 138017)
        if (data.already_granted) {
          toast.success('✅ Permission already granted! You can call this number directly.', {
            duration: 6000,
            icon: '📞',
            style: { background: '#1d4ed8', color: '#fff', fontWeight: 600 },
          });
          // Keep status as idle so user can directly click Call
        } else {
          setCallStatus('permission-sent');
          toast.success('Permission request sent! Waiting for user to accept...', {
            duration: 5000,
            icon: '📩',
            style: { background: '#25D366', color: '#fff', fontWeight: 600 },
          });
        }
      } else {
        toast.error(data.error?.message || data.error || 'Failed to send permission');
      }
    } catch (err) {
      const msg = err.message || 'Request failed';
      toast.error(msg);
      console.error('[WA Call] Permission error:', msg);
    } finally {
      setLoading(false);
    }
  };

  const handleCallClick = async () => {
    const clean = selectedCountry.code.replace('+', '') + sanitizeNumber(phoneNumber);
    const userType = customCredentials ? customCredentials.userType : ((user?.role === 'admin' || user?.role === 'super_admin') ? 'admin' : 'client');
    const userId = customCredentials ? customCredentials.userId : (user?.id || user?.client_id || user?.admin_id);

    console.log("[WA Call] User type:", userType, "User ID:", userId);
    console.log("[WA Call] Masking enabled:", masking);
    console.log("[WA Call] Display number:", getDisplayPhoneNumber(clean, masking));
    console.log("[WA Call] Actual number:", clean);

    if (!sanitizeNumber(phoneNumber)) {
      setError('Please enter a phone number');
      return;
    }
    if (!isValidE164(clean)) {
      setError('Invalid phone number for selected country');
      return;
    }

    setError('');
    setLoading(true);
    setCallStatus('connecting');

    try {
      await getLocalStream();
      const sdpOffer = await generateSdpOffer();

      const res = await fetch(`${WA_API_BASE}/api/wa-call/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          to: clean,
          sdpOffer,
          browserId: browserIdRef.current,
          userId,
          userType
        })
      });

      const data = await res.json();

      if (res.ok || data.success) {
        setCallId(data.call_id || data.callId);
        toast.success('Call initiated! Waiting for response...', {
          duration: 4000,
          icon: '📞',
          style: { background: '#25D366', color: '#fff', fontWeight: 600 },
        });
        setCallStatus('ringing');
      } else {
        toast.error(data.error?.message || data.error || 'Failed to initiate call');
        resetCallState();
      }
    } catch (err) {
      const msg = err.message || 'Call failed';
      toast.error(msg, { duration: 5000 });
      console.error('[WA Call] Call error:', msg);
      setCallStatus('error');
      cleanupWebRTC();
    } finally {
      setLoading(false);
    }
  };

  const handleHangup = async () => {
    try {
      if (callId) {
        const userType = customCredentials ? customCredentials.userType : ((user?.role === 'admin' || user?.role === 'super_admin') ? 'admin' : 'client');
        const userId = customCredentials ? customCredentials.userId : (user?.id || user?.client_id || user?.admin_id);

        const res = await fetch(`${WA_API_BASE}/api/wa-call/end`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ callId, userId, userType })
        });

        if (!res.ok) {
          const errData = await res.json();
          console.error('[WA Call] Remote hangup failed:', errData);

          let errorMsg = 'Failed to end call remotely. Please try again.';
          if (typeof errData === 'string') {
            errorMsg = errData;
          } else if (errData?.detail) {
            if (typeof errData.detail === 'string') {
              errorMsg = errData.detail;
            } else if (errData.detail?.error?.message) {
              errorMsg = typeof errData.detail.error.message === 'string' ? errData.detail.error.message : JSON.stringify(errData.detail.error.message);
            }
          }

          toast.error(errorMsg);
        } else {
          toast('Call ended', { icon: '🔴', duration: 2000 });
        }
      }
    } catch (err) {
      console.error('[WA Call] Hangup network error:', err.message);
      toast.error('Network error during hangup');
   } finally {
      resetCallState(true);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !loading) {
      if (callStatus === 'idle') {
        handleCallClick();
      }
    }
    if (e.key === 'Escape') {
      if (callStatus === 'idle') {
        setIsOpen(false);
      }
      if (showCountryDropdown) {
        setShowCountryDropdown(false);
      }
    }
  };

  // ✅ Handle paste event to preserve full number including country code
  const handlePaste = (e) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData('text');
    const clean = sanitizeNumber(pastedText);

    setPhoneNumber(clean);
    setError('');
  };

  const isInCall = ['connecting', 'ringing', 'connected'].includes(callStatus);

  const getInputDisplayValue = () => {
    if (!phoneNumber) return '';
    return phoneNumber;
  };

  // ── WhatsApp SVG Icon ──
  const WhatsAppIcon = () => (
    <svg viewBox="0 0 24 24" fill="currentColor">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
  );

  return (
    <div className="wa-call-fab-container" ref={panelRef}>
      <audio ref={audioRef} autoPlay playsInline style={{ display: 'none' }} />
      {/* ── Panel ── */}
      {isOpen && (
        <div className="wa-call-panel">
          {/* Header */}
          <div className="wa-call-panel-header">
            <span className="wa-call-panel-title">
              <WhatsAppIcon />
              WhatsApp Call
            </span>
            {!isInCall && (
              <button className="wa-call-panel-close" onClick={() => setIsOpen(false)}>
                <X />
              </button>
            )}
          </div>

          {/* Status Bar */}
          <div className={`wa-call-status-bar wa-status-${callStatus}`}>
            <span className="wa-call-status-dot" />
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
              <span className="wa-call-status-text">
                {callStatus === 'idle' && 'Ready'}
                {callStatus === 'permission-sent' && '📩 Awaiting permission...'}
                {callStatus === 'connecting' && '🔄 Connecting...'}
                {callStatus === 'ringing' && `🔔 Ringing... ${getDisplayPhoneNumber(selectedCountry.code.replace('+', '') + sanitizeNumber(phoneNumber), masking)}`}
                {callStatus === 'connected' && `✅ Connected with ${getDisplayPhoneNumber(selectedCountry.code.replace('+', '') + sanitizeNumber(phoneNumber), masking)}`}
                {callStatus === 'error' && '❌ Call failed'}
              </span>
            </div>
            {callStatus === 'connected' && (
              <span className="wa-call-timer">{formatDuration(callDuration)}</span>
            )}
          </div>

          {/* Body */}
          <div className="wa-call-panel-body">
            {/* Country Code Selector */}
            <div className="wa-call-country-selector">
              <label>Country Code</label>
              <div style={{ position: 'relative', marginBottom: '0.5rem' }} ref={dropdownRef}>
                <button
                  className="wa-call-country-button"
                  onClick={() => setShowCountryDropdown(!showCountryDropdown)}
                  disabled={isInCall}
                >
                  <span style={{ fontSize: '20px', marginRight: '8px' }}>{selectedCountry.flag}</span>
                  <span style={{ flex: 1, textAlign: 'left' }}>
                    {selectedCountry.country}
                  </span>
                  <ChevronDown size={16} style={{ transform: showCountryDropdown ? 'rotate(180deg)' : '', transition: 'transform 0.2s' }} />
                </button>

                {showCountryDropdown && (
                  <div className="wa-call-country-dropdown">
                    <input
                      type="text"
                      placeholder="Search country..."
                      className="wa-call-country-search"
                      value={countrySearch}
                      onChange={(e) => setCountrySearch(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                      autoFocus
                    />
                    <div className="wa-call-country-list">
                      {filteredCountries.length > 0 ? (
                        filteredCountries.map((country, idx) => (
                          <button
                            key={`${country.iso}-${idx}`}
                            className={`wa-call-country-option ${selectedCountry.iso === country.iso && selectedCountry.code === country.code ? 'active' : ''}`}
                            onClick={() => {
                              setSelectedCountry(country);
                              setShowCountryDropdown(false);
                              setCountrySearch('');
                              inputRef.current?.focus();
                            }}
                          >
                            <span style={{ fontSize: '18px', marginRight: '8px' }}>{country.flag}</span>
                            <span style={{ flex: 1, textAlign: 'left' }}>
                              <strong>{country.code}</strong> {country.country}
                            </span>
                          </button>
                        ))
                      ) : (
                        <div className="wa-call-country-empty">No countries found</div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Phone Number Input with Integrated Country Code */}
            <div className="wa-call-input-group">
              <label>Phone Number</label>
              <div className={`wa-call-phone-input-wrapper ${error ? 'wa-input-error' : ''} ${isInCall ? 'disabled' : ''}`} style={{ pointerEvents: isInCall ? 'none' : 'auto', opacity: isInCall ? 0.6 : 1 }}>
                <div className="wa-call-country-code-badge" onClick={() => !isInCall && setShowCountryDropdown(!showCountryDropdown)} title="Click to change country">
                  {selectedCountry.code}
                </div>
                <input
                  ref={inputRef}
                  type="tel"
                  className="wa-call-input"
                  placeholder="9876543210"
                  value={getInputDisplayValue()}
                  onChange={(e) => {
                    try {
                      // FIX: Simplified — just sanitize and set. Focus/blur handles masking display.
                      setPhoneNumber(sanitizeNumber(e.target.value));
                      setIsPrePopulated(false); // ✅ SECURE MASKING: Manual editing clears pre-populated state
                      setError('');
                    } catch (onChangeErr) {
                      console.error('[WA Call] onChange error:', onChangeErr);
                    }
                  }}
                  onFocus={() => setIsFocused(true)}
                  onBlur={() => setIsFocused(false)}
                  onPaste={handlePaste}
                  onKeyDown={handleKeyDown}
                  disabled={isInCall || loading}
                  readOnly={false}
                  style={{
                    letterSpacing: 'normal',
                    cursor: 'text'
                  }}
                />
              </div>
              {error ? (
                <div className="wa-call-error-text">{error}</div>
              ) : (
                <div className="wa-call-hint">
                  Enter phone number without country code
                </div>
              )}
            </div>

            {/* Action Buttons */}
            {!isInCall ? (
              <div className="wa-call-actions">
                <button
                  className="wa-call-btn wa-call-btn-call"
                  onClick={handleCallClick}
                  disabled={loading || !phoneNumber}
                >
                  <Phone size={16} />
                  {loading ? 'Connecting...' : 'Call'}
                </button>
                <button
                  className="wa-call-btn wa-call-btn-permission"
                  onClick={handleSendPermission}
                  disabled={loading || !phoneNumber}
                  title="Send call permission request first"
                >
                  <Shield size={16} />
                  Permission
                </button>
              </div>
            ) : (
              <div className="wa-call-in-call-controls">
                <button className="wa-call-btn wa-call-btn-hangup" onClick={handleHangup}>
                  <PhoneOff size={16} />
                  End Call
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── FAB Button ── */}
      <button
        className={`wa-call-fab ${isOpen ? 'wa-fab-active' : ''} ${isInCall ? 'wa-fab-in-call' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
        title="WhatsApp Call"
      >
        {loading && !isInCall ? (
          <div className="wa-call-fab-spinner" />
        ) : isInCall ? (
          <PhoneOff size={24} />
        ) : (
          <WhatsAppIcon />
        )}
      </button>
    </div>
  );
};

export default WhatsAppCallButton;